import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';

import {
  selectSubscriptionsState,
  filtersModalRulesSubscriptionChange,
  filtersModalConditionSubscriptionChange,
  resetModalSubscriptionChange,
  SUBSCRIPTIONS_LIMIT,
} from '@store';
import { showError } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';
import { getSubscriptionsQueryParams } from '@helpers';
import { availabilityServices, subscriptionsServices } from '@services';
import { useServicePoints, useAssignees } from '@hooks';
import { handleEnums } from 'hooks/availabilityTable/utils';
import {
  handleSubscriptionsData,
  handleAssigneesData,
  getSubscriptionsQueryParamsAndPayload,
} from './utils';

const MAP_ORDER_BY = {
  vehicleInternalId: 'vehicle.internalId',
  vehiclePlate: 'vehicle.plate',
};

export const useSubscriptionsOperations = () => {
  const { servicePointsMap, isFetched: isServicePointsFetched } =
    useServicePoints();
  const { filters } = useSelector(selectSubscriptionsState);
  const dispatch = useDispatch();

  const { assignees, assigneesMap, assigneesWithNone } = useAssignees();

  const router = useRouter();

  const {
    page = 1,
    pageSize = SUBSCRIPTIONS_LIMIT,
    orderBy = '',
    order = '',
    status,
    type,
    query,
    deliveryStatus,
    deliveryLocation,
    assignee,
    availabilityStatus,
    isErfuddAlert,
    customerTypes,
  } = router.query;

  const { cleanedParams, payload } = useMemo(
    () =>
      getSubscriptionsQueryParamsAndPayload({
        page,
        pageSize: parseInt(pageSize, 10) || SUBSCRIPTIONS_LIMIT,
        orderBy: MAP_ORDER_BY[orderBy] || orderBy || '',
        status,
        type,
        order,
        query,
        deliveryStatus,
        deliveryLocation,
        availabilityStatus,
        assignee,
        isErfuddAlert,
        filters,
        customerTypes,
        routerQuery: { ...router.query },
      }),
    [
      router.query,
      page,
      pageSize,
      orderBy,
      order,
      status,
      type,
      query,
      deliveryStatus,
      deliveryLocation,
      availabilityStatus,
      assignee,
      isErfuddAlert,
      filters,
      customerTypes,
    ]
  );
  const enumsResponse = useQuery([QUERY_KEYS.GET_SUBSCRIPTIONS_ENUMS_KEY], () =>
    subscriptionsServices.getSubscriptionsEnums()
  );

  const {
    data: availabilityEnumsData,
    isFetched: isAvailabilityEnumsIsFetched,
  } = useQuery(
    [QUERY_KEYS.GET_AVAILABILITY_ENUMS_KEY],
    () => availabilityServices.getAvailabilityEnums(),
    {
      enabled: !!router.isReady,
    }
  );

  const availabilityEnums = handleEnums(availabilityEnumsData);

  const subscriptionsResponse = useQuery(
    [
      QUERY_KEYS.GET_SUBSCRIPTIONS_KEY,
      { cleanedParams, filters: filters.modalFiltersRules },
      payload,
    ],
    () => {
      return subscriptionsServices.getSubscriptions({
        params: getSubscriptionsQueryParams(cleanedParams),
        payload,
      });
    },
    {
      enabled:
        router.isReady &&
        enumsResponse?.isFetched &&
        isAvailabilityEnumsIsFetched &&
        isServicePointsFetched,
    }
  );

  const subscriptions = handleSubscriptionsData(
    subscriptionsResponse?.data?.data || [],
    enumsResponse?.data?.data?.deliveryStatuses || [],
    servicePointsMap,
    assigneesMap,
    enumsResponse?.data?.data?.outOfSla || [],
    enumsResponse?.data?.data?.customerTypes || []
  );

  const subscriptionAssigneesOptions = useMemo(() => {
    return handleAssigneesData(assignees || []);
  }, [assignees]);

  const filtersModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionSubscriptionChange(conditions));
    },
    [dispatch]
  );

  const resetModalChange = useCallback(() => {
    dispatch(resetModalSubscriptionChange());
  }, [dispatch]);

  const handleChangeRules = (rules) => {
    dispatch(filtersModalRulesSubscriptionChange(rules));
  };

  useEffect(() => {
    if (subscriptionsResponse?.error) {
      showError(subscriptionsResponse.error);
    }
  }, [subscriptionsResponse?.error]);

  return {
    page,
    pageSize: parseInt(pageSize, 10) || SUBSCRIPTIONS_LIMIT,
    total: subscriptionsResponse?.data?.meta?.total,
    loading: subscriptionsResponse?.isFetching,
    subscriptions,
    subscriptionAssigneesOptions,
    assigneesWithNone,
    filters,
    enums: { ...enumsResponse?.data?.data, ...availabilityEnums } || {},
    subscriptionsQueryParams: cleanedParams,
    subscriptionsPayload: payload,
    getSubscriptions: subscriptionsResponse.refetch,
    filtersModalChange,
    resetModalChange,
    handleChangeRules,
  };
};
